import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import Input from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { usePostLogin } from '@/data/login';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import Error from '@/components/common/Error';
import Spinner from '@/components/common/Spinner';
import HorizontalLogo from '@/components/common/HorizontalLogo';

const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, { message: 'Password is required' })
});

const LoginForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const postLogin = usePostLogin();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { username: '', password: '' }
  });

  const onSubmit = async (values) => {
    setApiError('');
    setIsSubmitting(true);

    try {
      await postLogin(values);
      navigate('/');
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
    }
  };

  return (
    <Card className="w-screen max-w-[380px]">
      <CardHeader>
        <div className="p-6">
          <HorizontalLogo />
        </div>
        <CardTitle>Mega</CardTitle>
        <CardDescription>Log In</CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-2">
            <Input name="email" label="Email" />
            <Input name="password" label="Password" type="password" />
            {apiError && <Error error={apiError} />}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Spinner />}
              Login
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default LoginForm;
